import { useState } from "react";
import { motion } from "framer-motion";
import { Clock, Users, TrendingUp, Settings, ExternalLink, Copy, Check, Twitter, MessageCircle, Vote, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useLocale } from "@/contexts/LocaleContext";
import { useToast } from "@/hooks/use-toast";
import { getTimeRemaining, isDeadlinePassed as checkDeadlinePassed, getEventStatusDisplay } from "@/lib/date-utils";

interface PendingResolution {
  id: string;
  event_id: string;
  proposed_outcome: boolean;
  dispute_deadline: string | null;
  dispute_count: number;
}

interface EventCardProps {
  id: string;
  title: string;
  description: string;
  deadline: string;
  yesPool: number;
  noPool: number;
  totalBids: number;
  status: "OPEN" | "CLOSED" | "RESOLVED";
  language: string;
  index: number;
  isCreator?: boolean;
  isAdmin?: boolean;
  canDelete?: boolean;
  hasPendingResolution?: boolean;
  pendingResolution?: PendingResolution | null;
  userHasBid?: boolean;
  onBidClick: () => void;
  onResolveClick?: () => void;
  onVoteClick?: () => void;
  onDeleteClick?: () => void;
  isDeleting?: boolean;
}

const languageFlags: Record<string, string> = {
  en: "🇺🇸",
  pt: "🇧🇷",
  es: "🇪🇸",
  fr: "🇫🇷",
  de: "🇩🇪",
  it: "🇮🇹",
  global: "🌍",
};

const EventCard = ({
  id,
  title,
  description,
  deadline,
  yesPool,
  noPool,
  totalBids,
  status,
  language,
  index,
  isCreator,
  isAdmin,
  canDelete,
  hasPendingResolution,
  pendingResolution,
  userHasBid,
  onBidClick,
  onResolveClick,
  onVoteClick,
  onDeleteClick,
  isDeleting,
}: EventCardProps) => {
  const { t, formatCurrency, locale } = useLocale();
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  
  const totalPool = yesPool + noPool;
  const yesPercent = totalPool > 0 ? (yesPool / totalPool) * 100 : 50;
  const noPercent = totalPool > 0 ? (noPool / totalPool) * 100 : 50;

  const isDeadlinePassed = checkDeadlinePassed(deadline);

  const eventUrl = `${window.location.origin}/event/${id}`;
  const shareText = `${title} - ${locale.language === "pt" ? "Faça sua previsão no Callyout!" : "Make your prediction on Callyout!"}`;

  const statusDisplay = getEventStatusDisplay(status, deadline, locale.language as "en" | "pt");

  const handleCopyLink = async () => {
    await navigator.clipboard.writeText(eventUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast({
      title: locale.language === "pt" ? "Link copiado!" : "Link copied!",
      description: locale.language === "pt" ? "Compartilhe com seus amigos" : "Share it with your friends",
    });
    setShareOpen(false);
  };

  const handleShareTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(eventUrl)}`;
    window.open(url, "_blank");
    setShareOpen(false);
  };

  const handleShareWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(`${shareText} ${eventUrl}`)}`;
    window.open(url, "_blank");
    setShareOpen(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="glass-card-hover p-6 flex flex-col h-full cursor-pointer"
      onClick={onBidClick}
    >
      {/* Status Badge */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-lg">{languageFlags[language] || "🌍"}</span>
          <span
            className={`px-3 py-1 rounded-full text-xs font-semibold ${statusDisplay.style}`}
          >
            {statusDisplay.text}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 text-muted-foreground text-sm">
            <Clock size={14} />
            {getTimeRemaining(deadline, locale.language as "en" | "pt")}
          </div>
          <Popover open={shareOpen} onOpenChange={setShareOpen}>
            <PopoverTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8 text-muted-foreground hover:text-foreground"
                onClick={(e) => e.stopPropagation()}
              >
                <ExternalLink size={16} />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-48 p-2 bg-card border-border" align="end">
              <div className="space-y-1">
                <button
                  onClick={handleCopyLink}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-muted transition-colors"
                >
                  {copied ? <Check size={16} className="text-success" /> : <Copy size={16} />}
                  {locale.language === "pt" ? "Copiar link" : "Copy link"}
                </button>
                <button
                  onClick={handleShareTwitter}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-muted transition-colors"
                >
                  <Twitter size={16} />
                  Twitter / X
                </button>
                <button
                  onClick={handleShareWhatsApp}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-muted transition-colors"
                >
                  <MessageCircle size={16} />
                  WhatsApp
                </button>
              </div>
            </PopoverContent>
          </Popover>
          {/* Delete button for admins or creators with deletable events */}
          {(isAdmin || (canDelete && isCreator)) && onDeleteClick && (
            <button 
              onClick={(e) => {
                e.stopPropagation();
                onDeleteClick();
              }}
              disabled={isDeleting}
              className="p-1.5 rounded-lg hover:bg-destructive/10 transition-colors text-muted-foreground hover:text-destructive disabled:opacity-50"
              title={locale.language === "pt" ? "Excluir evento" : "Delete event"}
            >
              {isDeleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
            </button>
          )}
        </div>
      </div>

      {/* Title & Description */}
      <h3 className="text-lg font-bold text-foreground mb-2 line-clamp-2">
        {title}
      </h3>
      <p className="text-sm text-muted-foreground mb-4 line-clamp-2 flex-grow">
        {description}
      </p>

      {/* Pool Distribution Bar */}
      <div className="mb-4">
        <div className="flex justify-between text-sm mb-2">
          <span className="text-success font-semibold">
            {locale.language === "pt" ? "SIM" : "YES"} {yesPercent.toFixed(0)}%
          </span>
          <span className="text-destructive font-semibold">
            {locale.language === "pt" ? "NÃO" : "NO"} {noPercent.toFixed(0)}%
          </span>
        </div>
        <div className="h-2 bg-muted rounded-full overflow-hidden flex">
          <div
            className="h-full bg-gradient-to-r from-success to-success/70 transition-all duration-500"
            style={{ width: `${yesPercent}%` }}
          />
          <div
            className="h-full bg-gradient-to-r from-destructive/70 to-destructive transition-all duration-500"
            style={{ width: `${noPercent}%` }}
          />
        </div>
      </div>

      {/* Stats */}
      <div className="flex items-center justify-between text-sm text-muted-foreground mb-4">
        <div className="flex items-center gap-1">
          <TrendingUp size={14} />
          <span>{totalPool > 0 ? totalPool.toLocaleString() : "0"} {t("events.pool")}</span>
        </div>
        <div className="flex items-center gap-1">
          <Users size={14} />
          <span>{totalBids} {t("events.bids")}</span>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
        {status === "OPEN" ? (
          <>
            <Button 
              variant="success" 
              size="sm" 
              className="flex-1" 
              onClick={(e) => {
                e.stopPropagation();
                onBidClick();
              }}
            >
              {t("events.bidYes")}
            </Button>
            <Button 
              variant="danger" 
              size="sm" 
              className="flex-1" 
              onClick={(e) => {
                e.stopPropagation();
                onBidClick();
              }}
            >
              {t("events.bidNo")}
            </Button>
          </>
        ) : status === "CLOSED" && hasPendingResolution && pendingResolution ? (
          userHasBid && !isCreator ? (
            <Button 
              variant="outline" 
              size="sm" 
              className="flex-1 gap-2 border-amber-500/30 text-amber-500 hover:bg-amber-500/10" 
              onClick={(e) => {
                e.stopPropagation();
                onVoteClick?.();
              }}
            >
              <Vote size={16} />
              {locale.language === "pt" ? "Votar na Resolução" : "Vote on Resolution"}
            </Button>
          ) : (
            <div className="flex-1 text-center py-2 px-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <span className="text-amber-500 text-sm font-medium flex items-center justify-center gap-2">
                <Clock size={14} />
                {locale.language === "pt" ? "Aguardando aprovação" : "Pending approval"}
              </span>
            </div>
          )
        ) : status === "CLOSED" && isCreator ? (
          hasPendingResolution ? (
            <div className="flex-1 text-center py-2 px-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <span className="text-amber-500 text-sm font-medium flex items-center justify-center gap-2">
                <Clock size={14} />
                {locale.language === "pt" ? "Aguardando aprovação" : "Pending approval"}
              </span>
            </div>
          ) : (
            <Button 
              variant="hero" 
              size="sm" 
              className="flex-1 gap-2" 
              onClick={(e) => {
                e.stopPropagation();
                onResolveClick?.();
              }}
            >
              <Settings size={16} />
              {locale.language === "pt" ? "Resolver" : "Resolve"}
            </Button>
          )
        ) : (
          <div className="flex-1 text-center py-2 text-muted-foreground text-sm">
            {status === "RESOLVED" 
              ? (locale.language === "pt" ? "Evento finalizado" : "Event finished")
              : (locale.language === "pt" ? "Previsões encerradas" : "Predictions closed")}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default EventCard;
